import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { AppBar, Box, Button, Divider, FormControlLabel, IconButton, Switch, TextField, Toolbar, Typography, useMediaQuery, type Theme } from '@mui/material';
import { useFormik } from 'formik';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import * as yup from 'yup';
import { type Station } from '../../../../shared/types';
import { drawerStateAtom } from '../../../atoms';
import { useCreateAdminStation, useDeleteAdminStation, useUpdateAdminStation } from '../../../hooks/useAdminStations';
import { useDeleteDialog } from '../../../hooks/useDeleteDialog';
import { useSimpleAlert } from '../../../hooks/useSimpleAlert';
import { closedMixinToolbar, openedMixinToolbar } from '../../helpers';
import { useNavigate } from 'react-router';

type Props = {
    station?: Station | null;
};

const initialValues = {
    stationId: '',
    name: '',
    description: '',
    latitude: 0,
    longitude: 0,
    sortOrder: 0,
    spireUuid: '',
    active: true,
} as Station;

const validationSchema = yup.object().shape({
    stationId: yup.string().required('Required'),
    description: yup.string().required('Required'),
    name: yup.string().required('Required'),
    latitude: yup.number().required(),
    longitude: yup.number().required(),
    spireUuid: yup.string().optional(),
    sortOrder: yup.number().required(),
    active: yup.boolean().required(),
});

export const AdminStationForm = ({ station = null }: Props) => {
    const navigate = useNavigate();
    const [drawerState] = useAtom(drawerStateAtom);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const { mutate: updateAdminStation } = useUpdateAdminStation();
    const { mutate: createAdminStation } = useCreateAdminStation();
    const { mutate: deleteAdminStation } = useDeleteAdminStation();
    const { DeleteDialog, showDialog: showDeleteDialog } = useDeleteDialog();
    const { showSimpleAlert } = useSimpleAlert();

    const onBack = () => {
        navigate('/admin/stations');
    };

    const onSubmit = async (values: Station) => {
        const { _id } = values;
        const strippedValues = validationSchema.cast(values, { stripUnknown: true });

        if (_id) {
            updateAdminStation(
                { id: _id.toString(), data: strippedValues },
                {
                    onSuccess: (data) => {
                        showSimpleAlert({ title: 'Station successfully updated' });
                        formik.resetForm({ values: data });
                    },
                },
            );
        } else {
            createAdminStation(strippedValues, {
                onSuccess: () => {
                    showSimpleAlert({ title: 'Station successfully created' });
                    formik.resetForm({ values: initialValues });
                },
            });
        }
    };

    const formik = useFormik({ initialValues, validationSchema, onSubmit });

    const handleShowDeleteDialog = () => {
        showDeleteDialog({ title: `Confirm DELETE of station ${station?.stationId}`, onDelete: handleDeleteStation });
    };

    const handleDeleteStation = () => {
        if (station) {
            deleteAdminStation(station._id.toString(), {
                onSuccess: () => {
                    showSimpleAlert({ title: 'Station successfully deleted', severity: 'info' });
                    onBack();
                },
                onError: () => {
                    showSimpleAlert({ title: 'Unable to delete the station', severity: 'error' });
                },
            });
        }
    };

    useEffect(() => {
        const values = station ? { ...initialValues, ...station } : initialValues;

        formik.resetForm({ values });
    }, [station]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ maxWidth: (theme) => theme.breakpoints.values.sm, p: 3, pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 2 }}>
                        <Button variant="outlined" size="large" sx={{ minWidth: 0, pl: 1, pr: 1 }} onClick={onBack} startIcon={<NavigateBeforeIcon />}>
                            Stations
                        </Button>
                        {station && (
                            <Typography variant="h6" gutterBottom={false}>
                                {`${station.stationId}: ${station.name} - ${station.description}`}
                            </Typography>
                        )}
                    </Box>

                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Station Details
                    </Typography>
                    <Divider />
                </Box>
                <Box
                    sx={{
                        height: '100%',
                        overflowY: 'scroll',
                    }}>
                    <form noValidate autoComplete="off">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                maxWidth: (theme) => theme.breakpoints.values.sm,
                                p: 3,
                                mb: 8,
                            }}>
                            <TextField
                                aria-describedby="stationId-text"
                                label="Station Id"
                                placeholder="Station Id"
                                type="text"
                                name="stationId"
                                value={formik.values.stationId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.stationId)}
                                helperText={formik.touched.stationId && formik.errors.stationId}
                            />
                            <TextField
                                aria-describedby="name-text"
                                label="Name"
                                placeholder="Name"
                                type="text"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                            <TextField
                                aria-describedby="description-text"
                                label="Description"
                                placeholder="Description"
                                type="text"
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.description)}
                                helperText={formik.touched.description && formik.errors.description}
                            />
                            <TextField
                                aria-describedby="latitude-text"
                                label="Latitude"
                                placeholder="Latitude"
                                type="text"
                                name="latitude"
                                value={formik.values.latitude}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.latitude)}
                                helperText={formik.touched.latitude && formik.errors.latitude}
                            />
                            <TextField
                                aria-describedby="longitude-text"
                                label="Longitude"
                                placeholder="Longitude"
                                type="text"
                                name="longitude"
                                value={formik.values.longitude}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.longitude)}
                                helperText={formik.touched.longitude && formik.errors.longitude}
                            />
                            <TextField
                                aria-describedby="spireUuid-text"
                                label="Spire UUID"
                                placeholder="Spire UUID"
                                type="text"
                                name="spireUuid"
                                value={formik.values.spireUuid || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.spireUuid)}
                                helperText={formik.touched.spireUuid && formik.errors.spireUuid}
                            />
                            <FormControlLabel control={<Switch name="active" checked={formik.values.active} onChange={formik.handleChange} />} label="Active" />
                        </Box>
                    </form>
                </Box>
            </Box>
            <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: (theme) => theme.palette.grey[50] }}>
                <Toolbar
                    sx={(theme) => ({
                        ...(isNotSm &&
                            drawerState && {
                                ...openedMixinToolbar(theme),
                            }),
                        ...(isNotSm &&
                            !drawerState && {
                                ...closedMixinToolbar(theme),
                            }),
                        maxWidth: (theme) => theme.breakpoints.values.sm,
                    })}>
                    {station && (
                        <IconButton aria-label="delete" size="large" onClick={handleShowDeleteDialog}>
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={formik.submitForm} disabled={!formik.isValid || !formik.dirty}>
                        Save
                    </Button>
                </Toolbar>
            </AppBar>
            <DeleteDialog />
        </Box>
    );
};
